import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import {
  ApiError,
  NetworkError,
  VISIT_TEXT_MAX,
  vendorApi,
  type MeResponse,
  type OpenVisit,
  type SiteInfo,
  type VisitPayload,
} from '../lib/api';
import { OTHER_KEY, PURPOSES } from '../lib/purposes';

/**
 * The vendor phone flow.
 *
 * Recreates flow.jsx from the design handoff, minus everything that handoff
 * marks as demo scaffolding:
 *
 *   ✗ the iPhone bezel (ios-frame.jsx)      — this is a plain responsive page
 *   ✗ the "Who just scanned?" persona rail  — a testing affordance
 *   ✗ the "Demo — the emailed code" box     — the code ONLY arrives by email
 *   ✗ the DIRECTORY object                  — replaced by a real lookup
 *   ✗ the Vendor/Staff/QR tab bar           — three separate surfaces
 *
 * Screen names match the handoff's state machine exactly, so the two can be
 * read side by side.
 */

type Screen = 'loading' | 'badsite' | 'email' | 'code' | 'form' | 'in' | 'askout' | 'stay' | 'out';

interface Notice {
  tone: 'err' | 'warn' | 'info';
  icon: 'circle-alert' | 'triangle-alert' | 'info';
  text: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** The check-in fields, as `too_long` names them, in the vendor's words. */
const TOO_LONG_WHAT: Record<string, string> = {
  name: 'That name',
  company: 'That company name',
  otherText: 'That description',
};
const RESEND_COOLDOWN_SECONDS = 30;
/** The handoff's auto-submit delay once the sixth box fills. */
const AUTOSUBMIT_DELAY_MS = 160;

/**
 * Turn an API failure into something a vendor standing at a gate can act on.
 *
 * The handoff explicitly leaves these undesigned — "not yet designed, please
 * confirm handling: code expired, rate limit exceeded, email delivery failure,
 * network offline mid-flow". Each one gets a plain sentence that says what
 * happened and what to do next. None of them dead-end. An expired code is not
 * here: the API does not say a code expired, so it gets the code-error state
 * in verify() along with every other failed code.
 */
function describe(err: unknown): Notice {
  if (err instanceof NetworkError) {
    return {
      tone: 'warn',
      icon: 'triangle-alert',
      text: "We couldn't reach the network. Check your signal and try again — nothing was lost.",
    };
  }
  if (err instanceof ApiError) {
    switch (err.code) {
      // "Let the site office know you're here", here, for a mail failure and
      // on the bad-link screen — not "ask the site office to sign you in".
      // Staff have no way to sign a vendor in, so that was a dead end.
      case 'rate_limited_email':
        return {
          tone: 'warn',
          icon: 'triangle-alert',
          text: "Too many codes have been sent to this address in the last hour. Please wait a little while, or let the site office know you're here.",
        };
      case 'rate_limited_ip':
        return {
          tone: 'warn',
          icon: 'triangle-alert',
          text: 'Too many sign-in attempts from this connection. Please wait a little while and try again.',
        };
      // The per-IP ceilings on asking for a code and on checking one (a
      // one-minute window), and the per-session ones on signing in and out
      // (an hour): so neither "this connection" nor "a minute".
      case 'rate_limited':
        return {
          tone: 'warn',
          icon: 'triangle-alert',
          text: 'Too many attempts. Please wait a little while and try again.',
        };
      // Check-in refuses text wider than its column instead of cutting it short.
      // Say which field: resubmitting the same text only meets the same refusal.
      case 'too_long': {
        const what = TOO_LONG_WHAT[String(err.body.field)] ?? 'One of your answers';
        const max = typeof err.body.max === 'number' ? ` to ${err.body.max} characters or fewer` : '';
        return {
          tone: 'err',
          icon: 'circle-alert',
          text: `${what} is too long. Please shorten it${max} and try again.`,
        };
      }
      case 'mail_unavailable':
      case 'mail_failed':
        return {
          tone: 'err',
          icon: 'circle-alert',
          text: "We couldn't send your code just now. Please let the site office know you're here.",
        };
      case 'session_required':
        return {
          tone: 'info',
          icon: 'info',
          text: 'Your sign-in has expired. Please enter your email again.',
        };
      case 'site_unavailable':
      case 'unknown_site':
        return {
          tone: 'err',
          icon: 'circle-alert',
          text: 'This sign-in link is no longer valid. Please ask the site office for help.',
        };
      case 'no_open_visit':
        return {
          tone: 'info',
          icon: 'info',
          text: "You don't have an open visit to sign out of.",
        };
      default:
        break;
    }
  }
  return {
    tone: 'err',
    icon: 'circle-alert',
    text: 'Something went wrong. Please try again, or ask the site office for help.',
  };
}

// ───────────────────────────────────────────────────────────────── chrome

function Header({ site, showSite }: { site: SiteInfo | null; showSite: boolean }): JSX.Element {
  return (
    <div className="vs-hdr">
      <div className="vs-hdr-row">
        <div>
          <div className="vs-eyebrow">Vendor Check-In</div>
        </div>
        <img className="vs-logo" src="/assets/logo-white.png" alt="US Storage Centers" />
      </div>
      {showSite && site && (
        <div className="vs-site">
          <div className="vs-site-name">{site.name}</div>
          {site.addressLine && <div className="vs-site-meta">{site.addressLine}</div>}
        </div>
      )}
    </div>
  );
}

function Steps({ n, of = 3 }: { n: number; of?: number }): JSX.Element {
  return (
    <div className="vs-steps">
      {Array.from({ length: of }).map((_, i) => (
        <i key={i} className={i < n ? 'on' : ''} />
      ))}
    </div>
  );
}

function NoticeBox({ notice }: { notice: Notice }): JSX.Element {
  return (
    <div className={`vs-notice ${notice.tone}`} role="alert">
      <Icon name={notice.icon} size={16} />
      <span>{notice.text}</span>
    </div>
  );
}

function Summary({ rows, flat }: { rows: [string, string][]; flat?: boolean }): JSX.Element {
  return (
    <div
      className={`vs-card${flat ? ' flat' : ''}`}
      style={
        flat
          ? { background: 'var(--paper-100)', border: '1px solid var(--paper-200)' }
          : undefined
      }
    >
      {rows.map(([k, v]) => (
        <div className="vs-kv" key={k}>
          <span className="k">{k}</span>
          <span className="v">{v}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────── flow

export default function VendorFlow({ siteId }: { siteId: string }): JSX.Element {
  const [screen, setScreen] = useState<Screen>('loading');
  const [site, setSite] = useState<SiteInfo | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState('');
  const [emailErr, setEmailErr] = useState('');

  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [codeErr, setCodeErr] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [purpose, setPurpose] = useState('');
  const [otherText, setOtherText] = useState('');
  const [prefilled, setPrefilled] = useState(false);

  const [openVisit, setOpenVisit] = useState<OpenVisit | null>(null);
  const [signedIn, setSignedIn] = useState<VisitPayload | null>(null);
  const [propertyNotified, setPropertyNotified] = useState(false);
  const [signedOut, setSignedOut] = useState<{
    name: string;
    company: string;
    purpose: string;
    signedInAtLocal: string;
    signedOutAtLocal: string;
    dateLine: string;
  } | null>(null);
  const [receiptSent, setReceiptSent] = useState(false);

  // ── resolve the scanned site ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { site: resolved } = await vendorApi.getSite(siteId);
        if (cancelled) return;
        setSite(resolved);
        setScreen('email');
      } catch {
        if (!cancelled) setScreen('badsite');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  // ── resend cooldown ────────────────────────────────────────────────────
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  /** Post-authentication routing: open visit → sign-out prompt, else the form. */
  const routeAfterAuth = useCallback(async () => {
    const me: MeResponse = await vendorApi.me();
    if (me.openVisit) {
      setOpenVisit(me.openVisit);
      setScreen('askout');
      return;
    }
    if (me.lastVisit) {
      setName(me.lastVisit.name);
      setCompany(me.lastVisit.company);
      setPrefilled(true);
    }
    setScreen('form');
  }, []);

  const submitEmail = async (): Promise<void> => {
    const v = email.trim();
    if (!EMAIL_RE.test(v)) {
      setEmailErr('Enter a valid email address.');
      return;
    }
    setEmailErr('');
    setNotice(null);
    setBusy(true);
    try {
      const res = await vendorApi.requestCode(siteId, v);
      if (res.status === 'session') {
        // A live 24h session — skip the code step entirely.
        await routeAfterAuth();
        return;
      }
      setCode(['', '', '', '', '', '']);
      setCodeErr('');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setScreen('code');
      setTimeout(() => boxes.current[0]?.focus(), 120);
    } catch (err) {
      setNotice(describe(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (entered: string): Promise<void> => {
    setBusy(true);
    setCodeErr('');
    try {
      await vendorApi.verify(siteId, email.trim(), entered);
      setNotice(null);
      await routeAfterAuth();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_code') {
        // The handoff's code-error state: message, clear, focus box one. The
        // API answers every failed code this way — mistyped, expired, used or
        // out of guesses (vendor.ts) — so the words fit all four, where the
        // handoff's "doesn't match" fits only the first. It is the answer to
        // this attempt, so a notice left by an earlier one goes.
        setNotice(null);
        setCodeErr("That code didn't work. Check your latest email or resend.");
        setCode(['', '', '', '', '', '']);
        boxes.current[0]?.focus();
      } else {
        setNotice(describe(err));
        setCode(['', '', '', '', '', '']);
        boxes.current[0]?.focus();
      }
    } finally {
      setBusy(false);
    }
  };

  const setDigit = (i: number, val: string): void => {
    // Keep only the last typed character, per the handoff.
    const d = val.replace(/\D/g, '').slice(-1);
    setCodeErr('');
    setCode((prev) => {
      const next = [...prev];
      next[i] = d;
      if (d && i < 5) setTimeout(() => boxes.current[i + 1]?.focus(), 0);
      if (next.every((x) => x)) setTimeout(() => void verify(next.join('')), AUTOSUBMIT_DELAY_MS);
      return next;
    });
  };

  const onCodeKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && !code[i] && i > 0) boxes.current[i - 1]?.focus();
  };

  const onCodePaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    const d = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!d) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    d.split('').forEach((c, i) => {
      next[i] = c;
    });
    setCode(next);
    if (d.length === 6) setTimeout(() => void verify(d), AUTOSUBMIT_DELAY_MS);
    else boxes.current[d.length]?.focus();
  };

  const resend = async (): Promise<void> => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    setCodeErr('');
    setNotice(null);
    setCode(['', '', '', '', '', '']);
    try {
      await vendorApi.requestCode(siteId, email.trim());
      boxes.current[0]?.focus();
    } catch (err) {
      setNotice(describe(err));
    }
  };

  // The resolved purpose shown on the confirmation screen comes from the API
  // response, not from a local copy of the label — the server decides what was
  // actually recorded, and the two must never be able to disagree.
  const formValid =
    name.trim() && company.trim() && purpose && (purpose !== OTHER_KEY || otherText.trim());

  const signIn = async (): Promise<void> => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await vendorApi.checkin({
        name: name.trim(),
        company: company.trim(),
        purposeKey: purpose,
        otherText: otherText.trim(),
      });
      if (res.status === 'already_open') {
        // Already on site — route to the sign-out prompt instead.
        setOpenVisit({
          id: res.visit.id,
          name: res.visit.name,
          company: res.visit.company,
          purpose: res.visit.purpose,
          signedInAt: '',
          signedInAtLocal: res.visit.signedInAtLocal,
        });
        setScreen('askout');
        return;
      }
      setSignedIn(res.visit);
      setPropertyNotified(res.propertyNotified === true);
      setScreen('in');
    } catch (err) {
      setNotice(describe(err));
      if (err instanceof ApiError && err.status === 401) setScreen('email');
    } finally {
      setBusy(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await vendorApi.checkout();
      setSignedOut(res.visit);
      setReceiptSent(res.receiptSent);
      setScreen('out');
    } catch (err) {
      setNotice(describe(err));
      if (err instanceof ApiError && err.status === 401) setScreen('email');
    } finally {
      setBusy(false);
    }
  };

  // ── screens ────────────────────────────────────────────────────────────

  if (screen === 'loading') {
    return (
      <div className="vs-plain">
        <div className="vs-spinner" style={{ color: 'var(--us-blue)' }} />
      </div>
    );
  }

  if (screen === 'badsite') {
    return (
      <div className="vs-plain">
        <h1>This sign-in link isn't valid</h1>
        <p>
          The code you scanned doesn't match a US Storage Centers site. Please let the site
          office know you're here.
        </p>
      </div>
    );
  }

  if (screen === 'email') {
    return (
      <div className="vs">
        <Header site={site} showSite />
        <Steps n={1} />
        <div className="vs-body">
          <h1 className="vs-h1">Sign in to the site</h1>
          <p className="vs-p">
            Enter your email and we'll send you a 6-digit code. No account or password needed.
          </p>
          {notice && <NoticeBox notice={notice} />}
          <div className="vs-field">
            <label htmlFor="vs-email">Work email</label>
            <input
              id="vs-email"
              className={`vs-input${emailErr ? ' err' : ''}`}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailErr('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && void submitEmail()}
            />
            <span className={`vs-help${emailErr ? ' err' : ''}`}>
              {emailErr || "Used once to verify you — we don't store a password."}
            </span>
          </div>
          <div className="vs-foot">
            <button
              className="vs-btn primary"
              onClick={() => void submitEmail()}
              disabled={!email.trim() || busy}
              data-busy={busy}
            >
              {busy ? <span className="vs-spinner" /> : null}
              Send my code
              {!busy && <Icon name="arrow-right" size={17} />}
            </button>
            <p className="vs-legal">
              By signing in you agree to follow all posted site safety rules.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'code') {
    return (
      <div className="vs">
        <Header site={site} showSite={false} />
        <Steps n={2} />
        <div className="vs-body">
          <button
            className="vs-btn ghost"
            style={{ alignSelf: 'flex-start', width: 'auto', padding: 0, marginBottom: 12 }}
            onClick={() => setScreen('email')}
          >
            <Icon name="chevron-left" size={16} />
            Back
          </button>
          <h1 className="vs-h1">Check your email</h1>
          <p className="vs-p">
            We sent a 6-digit code to <b style={{ color: 'var(--us-navy)' }}>{email}</b>. It
            expires in 10 minutes.
          </p>
          {notice && <NoticeBox notice={notice} />}
          <div className={`vs-code${codeErr ? ' err' : ''}`} onPaste={onCodePaste}>
            {code.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  boxes.current[i] = el;
                }}
                value={d}
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                aria-label={`Digit ${i + 1}`}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onCodeKeyDown(i, e)}
              />
            ))}
          </div>
          {codeErr && (
            <span className="vs-help err" style={{ marginTop: 9, display: 'block' }}>
              {codeErr}
            </span>
          )}
          <div style={{ marginTop: 4 }}>
            <button className="vs-resend" disabled={cooldown > 0} onClick={() => void resend()}>
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
          </div>
          <div className="vs-foot">
            <button
              className="vs-btn primary"
              onClick={() => void verify(code.join(''))}
              disabled={!code.every((x) => x) || busy}
              data-busy={busy}
            >
              {busy ? <span className="vs-spinner" /> : null}
              Verify
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'form') {
    const firstName = name.trim().split(' ')[0] ?? '';
    return (
      <div className="vs">
        <Header site={site} showSite={false} />
        <Steps n={3} />
        <div className="vs-body">
          <h1 className="vs-h1">
            {prefilled && firstName ? `Welcome back, ${firstName}` : 'Tell us who you are'}
          </h1>
          <p className="vs-p">
            {prefilled
              ? 'We saved your details from your last visit — just confirm and pick why you’re here.'
              : "Three quick fields and you're on site."}
          </p>
          {notice && <NoticeBox notice={notice} />}

          <div className="vs-field">
            <label htmlFor="vs-name">
              Your name
              {prefilled && (
                <span className="vs-prefill" style={{ marginLeft: 8 }}>
                  <Icon name="check" size={11} />
                  Saved
                </span>
              )}
            </label>
            <input
              id="vs-name"
              className="vs-input"
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              maxLength={VISIT_TEXT_MAX.name}
            />
          </div>

          <div className="vs-field">
            <label htmlFor="vs-co">
              Company
              {prefilled && (
                <span className="vs-prefill" style={{ marginLeft: 8 }}>
                  <Icon name="check" size={11} />
                  Saved
                </span>
              )}
            </label>
            <input
              id="vs-co"
              className="vs-input"
              placeholder="Who do you work for?"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              autoComplete="organization"
              maxLength={VISIT_TEXT_MAX.company}
            />
          </div>

          <div className="vs-field">
            <label>What are you here for?</label>
            <div className="vs-purposes">
              {PURPOSES.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`vs-purpose${purpose === p.key ? ' sel' : ''}`}
                  aria-pressed={purpose === p.key}
                  onClick={() => setPurpose(p.key)}
                >
                  <span className="ic">
                    <Icon name={p.icon} size={16} />
                  </span>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {purpose === OTHER_KEY && (
            <div className="vs-field">
              <label htmlFor="vs-other">Briefly, what brings you here?</label>
              <input
                id="vs-other"
                className="vs-input"
                placeholder="e.g. Fire extinguisher service"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                maxLength={VISIT_TEXT_MAX.purpose}
              />
            </div>
          )}

          <div className="vs-foot">
            <button
              className="vs-btn primary"
              onClick={() => void signIn()}
              disabled={!formValid || busy}
              data-busy={busy}
            >
              {busy ? <span className="vs-spinner" /> : <Icon name="log-in" size={17} />}
              Sign in
            </button>
            {/* Said before the tap, not after: fair warning to every vendor,
                and to anyone signing in from a photo of the sign, a reason
                not to. Only when the site would actually email someone. */}
            {site?.notifiesProperty && (
              <p className="vs-legal">Signing in lets the property team know you're here.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'in' && signedIn) {
    return (
      <div className="vs">
        <Header site={site} showSite={false} />
        <div className="vs-body">
          <div className="vs-hero">
            <div className="ring ok">
              <Icon name="check" size={36} strokeWidth={2.6} />
            </div>
            <h2>You're signed in</h2>
            <p>{signedIn.dateLine}</p>
            <div className="vs-stamp">
              <Icon name="clock" size={14} />
              Signed in at {signedIn.signedInAtLocal}
            </div>
          </div>
          <Summary
            rows={[
              ['Name', signedIn.name],
              ['Company', signedIn.company],
              ['Purpose', signedIn.purpose],
              ['Site', signedIn.site.label ?? signedIn.site.name],
            ]}
          />
          <p className="vs-p" style={{ marginTop: 18, marginBottom: 0, fontSize: 13.5 }}>
            {/* Only when the API queued a notice — never on the strength of
                the form's promise alone. Worded for a queued notice, not a
                delivered one: the email goes out after this answer, and the
                API cannot know yet whether it arrived. */}
            {propertyNotified && "The property team is being told you're here. "}
            When you leave, scan the same sign and enter your email — you won't need a new code
            for 24 hours.
            {/* Said only when the API said when: the hour is server config. */}
            {signedIn.autoSignOutLocal &&
              ` If you forget, you'll be signed out automatically at ${signedIn.autoSignOutLocal}.`}
          </p>
          <div className="vs-foot">
            <button
              className="vs-btn secondary"
              onClick={() => {
                setOpenVisit({
                  id: signedIn.id,
                  name: signedIn.name,
                  company: signedIn.company,
                  purpose: signedIn.purpose,
                  signedInAt: '',
                  signedInAtLocal: signedIn.signedInAtLocal,
                });
                setScreen('askout');
              }}
            >
              <Icon name="log-out" size={17} />
              I'm leaving now
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'askout' && openVisit) {
    return (
      <div className="vs">
        <Header site={site} showSite={false} />
        <div className="vs-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16 }}>
            <span className="vs-badge on">
              <Icon name="circle-check" size={11} />
              On site
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--ink-500)' }}>
              since {openVisit.signedInAtLocal}
            </span>
          </div>
          <h1 className="vs-h1">Are you finished for the day?</h1>
          <p className="vs-p">We'll record your sign-out time and close out your visit.</p>
          {notice && <NoticeBox notice={notice} />}
          <Summary
            flat
            rows={[
              ['Name', openVisit.name],
              ['Company', openVisit.company],
              ['Purpose', openVisit.purpose],
              ['Signed in', openVisit.signedInAtLocal],
            ]}
          />
          <div className="vs-foot">
            <button
              className="vs-btn primary"
              onClick={() => void signOut()}
              disabled={busy}
              data-busy={busy}
            >
              {busy ? <span className="vs-spinner" /> : <Icon name="log-out" size={17} />}
              Yes, sign me out
            </button>
            <button
              className="vs-btn ghost"
              style={{ marginTop: 8 }}
              onClick={() => setScreen('stay')}
            >
              No, I'm still working
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'stay') {
    return (
      <div className="vs">
        <Header site={site} showSite={false} />
        <div className="vs-body">
          <div className="vs-hero">
            <div className="ring out">
              <Icon name="clock" size={34} />
            </div>
            <h2>Still on site</h2>
            <p>No problem — scan the sign again when you're done for the day.</p>
          </div>
          <div className="vs-foot">
            <button className="vs-btn secondary" onClick={() => setScreen('askout')}>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'out' && signedOut) {
    return (
      <div className="vs">
        <Header site={site} showSite={false} />
        <div className="vs-body">
          <div className="vs-hero">
            <div className="ring out">
              <Icon name="log-out" size={32} />
            </div>
            <h2>Signed out — thank you</h2>
            <p>{signedOut.dateLine}</p>
            <div className="vs-stamp">
              <Icon name="clock" size={14} />
              {signedOut.signedInAtLocal} → {signedOut.signedOutAtLocal}
            </div>
          </div>
          <Summary
            rows={[
              ['Name', signedOut.name],
              ['Company', signedOut.company],
              ['Purpose', signedOut.purpose],
              ['Visit closed', signedOut.signedOutAtLocal],
            ]}
          />
          {/* Only claimed when the send actually succeeded — the handoff's line
              states this as fact, and stating it falsely is worse than omitting it. */}
          {receiptSent && (
            <p className="vs-legal" style={{ marginTop: 18 }}>
              A copy of this visit record was emailed to {email}.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Any state we cannot render (a reload mid-flow) restarts cleanly rather than
  // showing a blank screen.
  return (
    <div className="vs-plain">
      <h1>Let's start again</h1>
      <p>Your session ended. Scan the sign to sign in or out.</p>
      <button
        className="vs-btn primary"
        style={{ maxWidth: 260 }}
        onClick={() => {
          setNotice(null);
          setScreen('email');
        }}
      >
        Start over
      </button>
    </div>
  );
}
